import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from './trpc.js';

const NO_STREAM = { context: { stream: false } };
const FEED_POLL_MS = 30_000;

export function useNotificationChannels(options: { enabled?: boolean; staleTime?: number } = {}) {
  const trpc = useTRPC();
  return useQuery({ ...trpc.notifications.channels.list.queryOptions(), ...options });
}

export function useNotificationPrefs() {
  const trpc = useTRPC();
  return useQuery(trpc.notifications.prefs.get.queryOptions());
}

export function useUpdateNotificationPrefs() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const queryKey = trpc.notifications.prefs.get.queryKey();
  return useMutation(
    trpc.notifications.prefs.update.mutationOptions({
      onMutate: async ({ role, prefs }) => {
        await queryClient.cancelQueries({ queryKey });
        const previous = queryClient.getQueryData(queryKey);
        queryClient.setQueryData(queryKey, (old) => (old ? { ...old, [role]: prefs } : old));
        return { previous };
      },
      onError: (_error, _vars, context) => {
        if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
      },
    }),
  );
}

export function useDisconnectSlack() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return useMutation(
    trpc.notifications.channels.slack.disconnect.mutationOptions({
      trpc: NO_STREAM,
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.notifications.channels.list.queryFilter());
      },
    }),
  );
}

export function useSlackTest() {
  const trpc = useTRPC();
  return useMutation(trpc.notifications.channels.slack.test.mutationOptions({ trpc: NO_STREAM }));
}

export function useSlackChannels(enabled: boolean) {
  const trpc = useTRPC();
  return useQuery(
    trpc.notifications.channels.slack.listChannels.queryOptions(undefined, {
      enabled,
      trpc: NO_STREAM,
    }),
  );
}

export function useSetSlackChannel() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return useMutation(
    trpc.notifications.channels.slack.setChannel.mutationOptions({
      trpc: NO_STREAM,
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.notifications.channels.list.queryFilter());
      },
    }),
  );
}

export function useSlackChannelManager(enabled: boolean) {
  const channelsQuery = useSlackChannels(enabled);
  const setChannel = useSetSlackChannel();

  return {
    channels: channelsQuery.data ?? [],
    isPending: channelsQuery.isPending,
    isFetching: channelsQuery.isFetching,
    isError: channelsQuery.isError,
    error: channelsQuery.error,
    refetch: channelsQuery.refetch,
    isSaving: setChannel.isPending,
    saveError: setChannel.error,
    resetSaveError: setChannel.reset,
    saveChannel: async (channelId: string): Promise<void> => {
      setChannel.reset();
      await setChannel.mutateAsync({ channelId });
    },
  };
}

export function useNotificationFeed() {
  const trpc = useTRPC();
  return useQuery(
    trpc.notifications.feed.list.queryOptions(undefined, {
      refetchInterval: FEED_POLL_MS,
      refetchOnWindowFocus: true,
      trpc: NO_STREAM,
    }),
  );
}

export function useMarkNotificationRead() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return useMutation(
    trpc.notifications.feed.markRead.mutationOptions({
      trpc: NO_STREAM,
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.notifications.feed.list.queryFilter());
      },
    }),
  );
}

export function useMarkAllNotificationsRead() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return useMutation(
    trpc.notifications.feed.markAllRead.mutationOptions({
      trpc: NO_STREAM,
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.notifications.feed.list.queryFilter());
      },
    }),
  );
}

export function useDismissNotification() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return useMutation(
    trpc.notifications.feed.dismiss.mutationOptions({
      trpc: NO_STREAM,
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.notifications.feed.list.queryFilter());
      },
    }),
  );
}
